import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { InView, EyebrowLabel } from "../utils/Animation";
import NotFound from "../pages/404";
import {
  INSIGHTS_REGISTRY,
  // ARTICLE_BY_SLUG,
  type ArticleMeta,
} from "../data/insights/insightsRegistry";

// ─── JSON data imports (CRA / Webpack — no import.meta.glob) ─────────
import typescriptMonoreposFintech from "../data/insights/typescript-monorepos-fintech.json";
import lowBandwidthUx from "../data/insights/low-bandwidth-ux.json";
import sprintFramework from "../data/insights/90-day-sprint-framework.json";
import launchKitLessons from "../data/insights/launch-kit-naija-lessons.json";
import nigerianDesignSystem from "../data/insights/nigerian-design-system.json";
import rtkQueryPatterns from "../data/insights/rtk-query-patterns.json";

// ─── Types ────────────────────────────────────────────────────────────
type BlockType = "paragraph" | "heading" | "subheading" | "quote" | "list";

interface BodyBlock {
  type: BlockType;
  content: string | string[];
}

interface InsightData {
  slug: string;
  tag: string;
  date: string;
  readTime: string;
  author: { name: string; role: string; avatar: string };
  title: string;
  excerpt: string;
  heroImage: string;
  body: BodyBlock[];
}

// ─── Lookup map ───────────────────────────────────────────────────────
// To add a new article: import the JSON above and add an entry here.
const INSIGHTS_MAP: Record<string, InsightData> = {
  "typescript-monorepos-fintech": typescriptMonoreposFintech as InsightData,
  "low-bandwidth-ux": lowBandwidthUx as InsightData,
  "90-day-sprint-framework": sprintFramework as InsightData,
  "launch-kit-naija-lessons": launchKitLessons as InsightData,
  "nigerian-design-system": nigerianDesignSystem as InsightData,
  "rtk-query-patterns": rtkQueryPatterns as InsightData,
};

// ─── Related articles ─────────────────────────────────────────────────
// Derived from the registry: exclude the current article, take the next 2.
function getRelated(currentSlug: string): ArticleMeta[] {
  return INSIGHTS_REGISTRY.filter((a) => a.slug !== currentSlug).slice(0, 2);
}

// ─── Body block renderer ──────────────────────────────────────────────
function Block({ block }: { block: BodyBlock }) {
  switch (block.type) {
    case "heading":
      return (
        <h2 className="font-serif text-3xl font-light text-itl-navy leading-snug mt-14 mb-5">
          {block.content as string}
        </h2>
      );
    case "subheading":
      return (
        <h3 className="font-serif text-xl font-light text-itl-navy mt-10 mb-4">
          {block.content as string}
        </h3>
      );
    case "paragraph":
      return (
        <p className="text-gray-600 leading-[1.85] text-[15px] mb-6">
          {block.content as string}
        </p>
      );
    case "quote":
      return (
        <blockquote className="my-10 pl-6 border-l-4 border-itl-blue">
          <p className="font-serif text-2xl font-light text-itl-navy leading-snug italic">
            {block.content as string}
          </p>
        </blockquote>
      );
    case "list":
      return (
        <ul className="mb-6 flex flex-col gap-3 mt-2">
          {(block.content as string[]).map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 bg-itl-blue rounded-full shrink-0" />
              <span className="text-gray-600 text-[15px] leading-relaxed">
                {item}
              </span>
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

// ─── Reading progress bar ─────────────────────────────────────────────
function ReadingProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? (el.scrollTop / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed top-[72px] left-0 right-0 h-[2px] bg-gray-100 z-40">
      <motion.div
        className="h-full bg-itl-blue origin-left"
        style={{ scaleX: progress / 100 }}
        transition={{ duration: 0.05 }}
      />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────
export default function SingleInsight() {
  const { slug } = useParams<{ slug: string }>();
  const insight = slug ? (INSIGHTS_MAP[slug] ?? null) : null;

  if (!insight) {
    return (
      <NotFound
        heading="Article not found."
        message="This article may have moved or been unpublished."
        backLabel="Back to all insights"
        backHref="/insights"
      />
    );
  }

  const related = getRelated(insight.slug);

  return (
    <>
      <ReadingProgress />

      {/* ── Hero ── */}
      <div className="relative bg-itl-navy pt-40 pb-28 overflow-hidden">
        <img
          src={insight.heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-itl-navy/50 to-itl-navy" />

        <div className="relative max-w-3xl mx-auto px-6 lg:px-0">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 mb-8"
          >
            <Link
              to="/insights"
              className="text-[11px] uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors"
            >
              Insights
            </Link>
            <span className="text-white/20 text-xs">/</span>
            <span className="text-[11px] uppercase tracking-widest text-itl-blue">
              {insight.tag}
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4"
          >
            {insight.tag}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.85,
              ease: [0.22, 1, 0.36, 1],
              delay: 0.25,
            }}
            className="font-serif text-4xl lg:text-5xl font-light text-white leading-[1.1] mb-6"
          >
            {insight.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
            className="text-white/55 text-base leading-relaxed mb-10 max-w-2xl"
          >
            {insight.excerpt}
          </motion.p>

          {/* Author + meta */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 shrink-0">
              <img
                src={insight.author.avatar}
                alt={insight.author.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-white text-[13px] font-semibold">
                {insight.author.name}
              </p>
              <p className="text-white/40 text-[11px]">{insight.author.role}</p>
            </div>
            <div className="ml-auto flex items-center gap-4 text-white/30 text-[11px]">
              <span>{insight.date}</span>
              <span className="w-px h-3 bg-white/20" />
              <span>{insight.readTime}</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Article body ── */}
      <article className="bg-white pt-16 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-0">
          {insight.body.map((block, i) => (
            <InView key={i} delay={0} margin="-20px">
              <Block block={block} />
            </InView>
          ))}
        </div>

        {/* Share + tag bar */}
        <div className="max-w-3xl mx-auto px-6 lg:px-0 mt-16 pt-10 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <span className="inline-block px-4 py-2 border border-itl-blue/20 bg-itl-blue/5 text-itl-blue text-[11px] font-semibold uppercase tracking-widest">
            {insight.tag}
          </span>
          <div className="flex items-center gap-3">
            <p className="text-gray-400 text-[11px] uppercase tracking-widest">
              Share:
            </p>
            {["Twitter", "LinkedIn", "Copy link"].map((label) => (
              <button
                key={label}
                className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 hover:text-itl-navy transition-colors px-3 py-1.5 border border-gray-200 hover:border-gray-400"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Author bio */}
        <div className="max-w-3xl mx-auto px-6 lg:px-0 mt-12">
          <div className="flex gap-5 p-8 bg-[#f7f7f5]">
            <div className="w-16 h-16 rounded-full overflow-hidden shrink-0">
              <img
                src={insight.author.avatar}
                alt={insight.author.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-semibold text-itl-navy mb-0.5">
                {insight.author.name}
              </p>
              <p className="text-[12px] text-itl-blue uppercase tracking-widest mb-3">
                {insight.author.role}
              </p>
              <p className="text-gray-500 text-[13px] leading-relaxed">
                {insight.author.role} — building
                resilient web and mobile solutions for the African market.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* ── Related articles (from registry — always up to date) ── */}
      {related.length > 0 && (
        <section className="bg-[#f7f7f5] py-20">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <InView className="mb-10">
              <EyebrowLabel>Continue Reading</EyebrowLabel>
            </InView>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200">
              {related.map((a, i) => (
                <InView
                  key={a.slug}
                  delay={i * 0.08}
                  className="bg-white group"
                >
                  <Link to={a.href} className="flex h-full">
                    <div className="relative w-36 lg:w-48 shrink-0 overflow-hidden">
                      <img
                        src={a.image}
                        alt={a.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-7 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-itl-blue">
                            {a.tag}
                          </span>
                          <span className="text-gray-300 text-xs">/</span>
                          <span className="text-gray-400 text-[11px]">
                            {a.date}
                          </span>
                        </div>
                        <h3 className="font-serif text-[17px] font-light text-itl-navy leading-snug group-hover:text-itl-blue transition-colors">
                          {a.title}
                        </h3>
                      </div>
                      <p className="mt-4 text-[11px] font-semibold uppercase tracking-widest text-itl-blue flex items-center gap-1.5">
                        Read{" "}
                        <span className="group-hover:translate-x-1 transition-transform inline-block">
                          →
                        </span>
                      </p>
                    </div>
                  </Link>
                </InView>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Back link ── */}
      <section className="bg-white py-12">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-widest text-itl-navy hover:text-itl-blue transition-colors group"
          >
            <span className="group-hover:-translate-x-1 transition-transform inline-block">
              ←
            </span>
            Back to all insights
          </Link>
        </div>
      </section>
    </>
  );
}
